import React, { useState } from 'react'
import { supabase } from '../lib/supabase'
import ReviewPermission from './ReviewPermission'
import ManualReviewRequest from './ManualReviewRequest'

const BIKE_CATEGORY_OPTIONS = ['', 'XC', 'trail', 'enduro', 'DH', 'eMTB']
const WHEEL_SETUP_OPTIONS = ['', '29', 'mullet', '27.5']
const PROTECTED_JOB_FIELDS = `
  id, payment_status, payment_paid_at, payment_link, stripe_checkout_session_id,
  stripe_payment_intent_id, stripe_payment_link_id, payment_reference,
  invoice_number, report_sent_at
`
const CUSTOMER_BIKE_FIELDS = ['brand', 'model', 'year', 'bike_type', 'bike_size', 'wheel_setup']

function splitFullName(value) {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return { first_name: '', last_name: '' }
  if (parts.length === 1) return { first_name: parts[0], last_name: '' }
  return { first_name: parts.slice(0, -1).join(' '), last_name: parts[parts.length - 1] }
}

function cleanBikeValue(value) {
  return String(value || '').trim()
}

function compareBikeValue(value) {
  return cleanBikeValue(value).toLowerCase()
}

function customerBikePayloadFromForm(form, workshopId, customerId) {
  if (!workshopId || !customerId) return null
  const payload = {
    workshop_id: workshopId,
    customer_id: customerId,
    brand: cleanBikeValue(form.bike_brand),
    model: cleanBikeValue(form.bike_model),
    year: cleanBikeValue(form.bike_year),
    bike_type: cleanBikeValue(form.bike_type),
    bike_size: cleanBikeValue(form.bike_size),
    wheel_setup: cleanBikeValue(form.wheel_setup),
  }
  return CUSTOMER_BIKE_FIELDS.some(field => Boolean(payload[field])) ? payload : null
}

function customerBikePayloadFromCustomer(customer, workshopId, customerId = customer?.id) {
  if (!customer) return null
  return customerBikePayloadFromForm({
    bike_brand: customer.bike_brand,
    bike_model: customer.bike_model,
    bike_year: customer.bike_year,
    bike_type: customer.bike_type,
    bike_size: customer.bike_size,
    wheel_setup: customer.wheel_setup,
  }, workshopId, customerId)
}

function bikeMatchesPayload(bike, payload) {
  if (!bike?.id || !payload) return false
  if (String(bike.workshop_id || '') !== String(payload.workshop_id || '')) return false
  if (String(bike.customer_id || '') !== String(payload.customer_id || '')) return false
  return CUSTOMER_BIKE_FIELDS.every(field => compareBikeValue(bike[field]) === compareBikeValue(payload[field]))
}

function bikeCompatibleWithPayload(bike, payload) {
  if (!bike?.id || !payload) return false
  if (String(bike.workshop_id || '') !== String(payload.workshop_id || '')) return false
  if (String(bike.customer_id || '') !== String(payload.customer_id || '')) return false
  const identityFields = ['brand', 'model', 'year']
  const hasIdentity = identityFields.some(field => Boolean(payload[field]))
  if (!hasIdentity) return bikeMatchesPayload(bike, payload)
  return CUSTOMER_BIKE_FIELDS.every(field => {
    const bikeValue = compareBikeValue(bike[field])
    const payloadValue = compareBikeValue(payload[field])
    return !bikeValue || !payloadValue || bikeValue === payloadValue
  })
}

function nonBlankBikePayload(payload) {
  return CUSTOMER_BIKE_FIELDS.reduce((acc, field) => {
    const value = cleanBikeValue(payload?.[field])
    if (value) acc[field] = value
    return acc
  }, {})
}

function hasProtectedJobMarkers(job = {}) {
  return job.payment_status === 'paid'
    || Boolean(job.payment_paid_at)
    || Boolean(job.payment_link)
    || Boolean(job.stripe_checkout_session_id)
    || Boolean(job.stripe_payment_intent_id)
    || Boolean(job.stripe_payment_link_id)
    || Boolean(job.payment_reference)
    || Boolean(job.invoice_number)
    || Boolean(job.report_sent_at)
}

async function hasProtectedJobHistory(jobIds = []) {
  if (!jobIds.length) return false
  const checks = [
    supabase.from('job_report_versions').select('id', { count: 'exact', head: true }).in('job_id', jobIds),
    supabase.from('audit_events').select('id', { count: 'exact', head: true }).eq('entity_type', 'job').in('entity_id', jobIds),
    supabase.from('notification_log').select('id', { count: 'exact', head: true }).in('job_id', jobIds),
  ]
  const results = await Promise.all(checks)
  const failed = results.find(result => result.error)
  if (failed) throw failed.error
  return results.some(result => (result.count || 0) > 0)
}

export default function CustomerModal({ workshopId, onClose, onSaved, onDeleted, initialData }) {
  const isEdit = !!initialData
  const [form, setForm] = useState(initialData ? {
    first_name: initialData.first_name || '',
    last_name: initialData.last_name || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    bike_brand: initialData.bike_brand || '',
    bike_model: initialData.bike_model || '',
    bike_year: initialData.bike_year || '',
    bike_type: initialData.bike_type || '',
    bike_size: initialData.bike_size || '',
    wheel_setup: initialData.wheel_setup || '',
    notes: initialData.notes || ''
  } : { full_name: '', first_name: '', last_name: '', email: '', phone: '', bike_brand: '', bike_model: '', bike_year: '', bike_type: '', bike_size: '', wheel_setup: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState(null)

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function ensureCustomerBikeRow(customerId) {
    const payload = customerBikePayloadFromForm(form, workshopId, customerId)
    if (!payload) return null

    const { data: existingRows, error: existingError } = await supabase
      .from('bikes')
      .select('*')
      .eq('workshop_id', workshopId)
      .eq('customer_id', customerId)
      .order('created_at', { ascending: true })
    if (existingError) throw existingError

    const existingMatch = (existingRows || []).find(row => bikeMatchesPayload(row, payload))
    if (existingMatch) return existingMatch

    const previousPayload = initialData
      ? customerBikePayloadFromCustomer(initialData, workshopId, customerId)
      : null
    const previousMatch = previousPayload
      ? (existingRows || []).find(row => bikeMatchesPayload(row, previousPayload) || bikeCompatibleWithPayload(row, previousPayload))
      : null
    const compatibleMatch = previousMatch
      || (existingRows || []).find(row => bikeCompatibleWithPayload(row, payload))

    if (compatibleMatch) {
      const updatePayload = nonBlankBikePayload(payload)
      if (!Object.keys(updatePayload).length) return compatibleMatch
      const { data: updatedBike, error: updateError } = await supabase
        .from('bikes')
        .update(updatePayload)
        .eq('workshop_id', workshopId)
        .eq('customer_id', customerId)
        .eq('id', compatibleMatch.id)
        .select('*')
        .single()
      if (updateError) throw updateError
      return updatedBike
    }

    const { data: insertedBike, error: insertError } = await supabase
      .from('bikes')
      .insert({
        ...payload,
        verification_state: 'workshop_verified',
        raw_submitted_data: {
          source: 'customer_profile_fields',
          customer_id: customerId,
        },
      })
      .select('*')
      .single()
    if (insertError) throw insertError
    return insertedBike
  }

  async function save() {
    if (isEdit && !form.first_name.trim()) { setError('Customer name required.'); return }
    if (!isEdit && !form.full_name.trim()) { setError('Customer name required.'); return }
    setSaving(true)
    setError(null)
    try {
      let savedCustomer
      if (isEdit) {
        const { data, error: updateError } = await supabase
          .from('customers')
          .update(form)
          .eq('id', initialData.id)
          .select('*')
          .single()
        if (updateError) throw updateError
        savedCustomer = data
      } else {
        const { full_name, ...payload } = form
        const { data, error: insertError } = await supabase
          .from('customers')
          .insert({ ...payload, ...splitFullName(full_name), workshop_id: workshopId })
          .select('*')
          .single()
        if (insertError) throw insertError
        savedCustomer = data
      }

      await ensureCustomerBikeRow(savedCustomer.id)
      onSaved()
    } catch (err) {
      setError(err.message || 'Could not save customer.')
    } finally {
      setSaving(false)
    }
  }

  async function doDelete() {
    setDeleting(true)
    setError(null)
    const { data: jobs, error: jobsError } = await supabase
      .from('jobs')
      .select(PROTECTED_JOB_FIELDS)
      .eq('customer_id', initialData.id)
    if (jobsError) {
      setDeleting(false)
      setError(jobsError.message)
      return
    }
    const jobRows = jobs || []
    const protectedByMarker = jobRows.some(hasProtectedJobMarkers)
    const protectedByHistory = !protectedByMarker && await hasProtectedJobHistory(jobRows.map(job => job.id))
    if (protectedByMarker || protectedByHistory) {
      setDeleting(false)
      setConfirmDelete(false)
      setError('This customer has protected service, payment, invoice, report, or audit history. Keep the customer record and cancel/void individual jobs instead.')
      return
    }

    const { error: err } = await supabase.from('customers').delete().eq('id', initialData.id)
    setDeleting(false)
    if (err) setError(err.message)
    else onDeleted()
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Customer' : 'Add Customer'}</div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}
        {isEdit ? (
          <div className="grid-2">
            <div className="field"><label>First Name</label><input value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="James" /></div>
            <div className="field"><label>Last Name</label><input value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Wilson" /></div>
          </div>
        ) : (
          <div className="field"><label>Full Name</label><input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="James Wilson" autoFocus /></div>
        )}
        <div className="grid-2">
          <div className="field"><label>Email</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="james@example.com" /></div>
          <div className="field"><label>Phone</label><input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="07700 900000" /></div>
        </div>
        {isEdit ? (
          <>
        <div className="sec">Bike</div>
        <div className="grid-3">
          <div className="field"><label>Bike Brand</label><input value={form.bike_brand} onChange={e => set('bike_brand', e.target.value)} placeholder="Santa Cruz" /></div>
          <div className="field"><label>Model</label><input value={form.bike_model} onChange={e => set('bike_model', e.target.value)} placeholder="Nomad" /></div>
          <div className="field"><label>Year</label><input value={form.bike_year} onChange={e => set('bike_year', e.target.value)} inputMode="numeric" placeholder="2024" /></div>
        </div>
        <div className="grid-3">
          <div className="field"><label>Bike Category</label>
            <select value={form.bike_type} onChange={e => set('bike_type', e.target.value)}>
              {BIKE_CATEGORY_OPTIONS.map(option => <option key={option || 'blank'} value={option}>{option || 'Select category...'}</option>)}
            </select>
          </div>
          <div className="field"><label>Bike Size</label><input value={form.bike_size} onChange={e => set('bike_size', e.target.value)} placeholder="M, L, XL..." /></div>
          <div className="field"><label>Wheel Setup</label>
            <select value={form.wheel_setup} onChange={e => set('wheel_setup', e.target.value)}>
              {WHEEL_SETUP_OPTIONS.map(option => <option key={option || 'blank'} value={option}>{option || 'Select wheel setup...'}</option>)}
            </select>
          </div>
        </div>
        <div className="field"><label>Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Riding style, preferences..." /></div>
          </>
        ) : (
          <details className="intake-details">
            <summary>Optional bike/details</summary>
            <div className="grid-3" style={{ marginTop: 12 }}>
              <div className="field"><label>Bike Brand</label><input value={form.bike_brand} onChange={e => set('bike_brand', e.target.value)} placeholder="Santa Cruz" /></div>
              <div className="field"><label>Model</label><input value={form.bike_model} onChange={e => set('bike_model', e.target.value)} placeholder="Nomad" /></div>
              <div className="field"><label>Year</label><input value={form.bike_year} onChange={e => set('bike_year', e.target.value)} inputMode="numeric" placeholder="2024" /></div>
            </div>
            <div className="grid-3">
              <div className="field"><label>Bike Category</label>
                <select value={form.bike_type} onChange={e => set('bike_type', e.target.value)}>
                  {BIKE_CATEGORY_OPTIONS.map(option => <option key={option || 'blank'} value={option}>{option || 'Select category...'}</option>)}
                </select>
              </div>
              <div className="field"><label>Bike Size</label><input value={form.bike_size} onChange={e => set('bike_size', e.target.value)} placeholder="M, L, XL..." /></div>
              <div className="field"><label>Wheel Setup</label>
                <select value={form.wheel_setup} onChange={e => set('wheel_setup', e.target.value)}>
                  {WHEEL_SETUP_OPTIONS.map(option => <option key={option || 'blank'} value={option}>{option || 'Select wheel setup...'}</option>)}
                </select>
              </div>
            </div>
            <div className="field"><label>Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Riding style, preferences..." /></div>
          </details>
        )}

        {initialData?.id && <ReviewPermission key={initialData.id} workshopId={workshopId} customerId={initialData.id} />}
        {initialData?.id && <ManualReviewRequest workshopId={workshopId} customerId={initialData.id} />}
        {!initialData?.id && <p className="text-muted fs12">Optional review email preferences can be recorded after saving the customer.</p>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={save} disabled={saving || deleting}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Customer'}</button>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          </div>
          {isEdit && !confirmDelete && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => setConfirmDelete(true)}>Delete Customer</button>
          )}
          {isEdit && confirmDelete && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="fs12 text-muted">Delete customer and unprotected draft/test data?</span>
              <button className="btn btn-sm" style={{ background: 'var(--red)', color: '#fff' }} onClick={doDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Yes, delete'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>No</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
